/* ============================================================
   TPW PDP v9 - interactions, ported from pdp-v9a.html and rewired
   to real, server-rendered DOM + a {{ product.variants | json }} blob.
   Self-contained, scoped to [data-pdpv9]. No framework.

   What it does:
   - clean cross-fade image gallery: touch/pointer swipe + thumbnails +
     ArrowLeft/ArrowRight keyboard nav, looping from both ends (flat image,
     object-fit:contain, no tilt, no auto-zoom)
   - click-to-zoom lightbox with focus trap + Escape + arrow nav
   - bespoke radio swatch variant selector (roving tabindex + arrow keys),
     updating the hidden form id input, price, save, BNPL, free-ship, gallery
     cross-fade, and the mobile sticky bar, with ZERO layout shift
   - real add-to-cart (AJAX /cart/add.js + cart-drawer refresh) with calm
     Adding -> Added states; native form submit as the no-JS fallback
   - mobile sticky bar visibility via IntersectionObserver
   - CRO-02 desktop sticky buy strip (>=990px) on the same twin-sentinel pattern
   ============================================================ */
(function () {
  "use strict";
  if (window.__pdpv9Init) return;
  window.__pdpv9Init = true;

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    var roots = document.querySelectorAll("[data-pdpv9]");
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  });

  // FUNC-01: re-init when the PDP section is re-rendered (Shopify theme editor edits / section
  // reload). This listener is registered once and persists on document; the per-root
  // root.__pdpv9 guard inside initRoot makes it idempotent, so gallery/variant/ATC controls
  // come back to life on the fresh DOM instead of going dead until a full page reload.
  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector("[data-pdpv9]");
    if (root) initRoot(root);
  });

  function initRoot(root) {
    if (root.__pdpv9) return;
    root.__pdpv9 = true;

    var $ = function (s) { return root.querySelector(s); };
    var $$ = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

    var stage = $("[data-stage]");
    var slides = $$("[data-slide]");
    var thumbs = $$("[data-thumb]");
    var strip = $("[data-strip]");
    var gPrev = $("[data-g-prev]");          // gallery prev arrow (restored 2026-07-04)
    var gNext = $("[data-g-next]");          // gallery next arrow
    var navDir = 0;                          // slide+fade direction: +1 fwd, -1 back, 0 = fade only
    var imgs = $$(".obj-img");
    var nImages = slides.length;
    var gCount = $("[data-gframe-count]");   // G1 frame counter
    var gTicks = $$(".gtick");               // G1 position ticks

    // map media id -> slide index, so a variant's image can be found wherever it sits
    // in the gallery (variant images often live beyond the first few media).
    var mediaIndexById = {};
    for (var mi = 0; mi < slides.length; mi++) {
      var mid = slides[mi].getAttribute("data-media-id");
      if (mid) mediaIndexById[mid] = mi;
    }

    // start on the server-rendered hero slide (the selected variant's image), not always 0
    var initFrame = 0;
    for (var si = 0; si < slides.length; si++) {
      if (slides[si].classList.contains("on")) { initFrame = si; break; }
    }
    var state = { frame: initFrame, selVar: 0 };

    /* ---- variants data (server-rendered) ---- */
    var variants = [];
    try {
      var blob = $("[data-pdpv9-variants]");
      if (blob) variants = JSON.parse(blob.textContent) || [];
    } catch (e) { variants = []; }
    var invMap = {};
    try { var iblob = $("[data-pdpv9-inv]"); if (iblob) invMap = JSON.parse(iblob.textContent) || {}; } catch (e2) { invMap = {}; }
    // selected variant index = the one whose id matches the hidden input
    var variantInput = $("[data-variant-input]");
    if (variantInput && variants.length) {
      var sid = parseInt(variantInput.value, 10);
      for (var vi = 0; vi < variants.length; vi++) {
        if (variants[vi].id === sid) { state.selVar = vi; break; }
      }
    }

    var fmtMoney = function (cents) {
      var n = (cents || 0) / 100;
      return "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    // "Engraved Figure": split currency mark / dollars run / dotless cents so each is styled
    // separately. Mirrors the Liquid first-paint markup (.cur + [data-price-d] + .cents).
    var priceHtml = function (cents) {
      var s = fmtMoney(cents);            // "$599.95"
      var sym = s.charAt(0);              // "$"
      // F-02: only UNDER $1 (cents < 100) render whole full-size — below a dollar the dollars
      // run would be a bare "$0" beside a tiny superscript. $1+ keeps the prestige superscript.
      if ((cents || 0) < 100) return '<span class="cur">' + sym + '</span><span data-price-d>' + s.slice(1) + '</span>';
      return '<span class="cur">' + sym + '</span><span data-price-d>' + s.slice(1, -3) + '</span><span class="cents">' + s.slice(-2) + '</span>';
    };

    /* ---- F-24: digit-roll the displayed prices (shared TPWRoll odometer) ----
       The roll only DECORATES the transition between two values that the existing
       price logic already computed (fmtMoney/priceHtml); it never changes the value.
       The dollars run ([data-price-d]) is the headline numeral that rolls; the tiny
       superscript .cents are committed instantly (rolling half-size cents reads as
       noise, not prestige). The ATC "Total" is a flat string -> rolls whole.
       Reduced-motion: TPWRoll falls back to an instant reel-shaped set. */
    var _hasRoll = function () { return !!window.TPWRoll; };
    var _lastDollars = null;   // last dollars-run text for the main price reel
    var _lastTotal = null;     // last ATC "Total" text for the total reel
    var _lastBnpl = null;      // last BNPL per-payment text for the count-tween
    // Split fmtMoney output into the dollars run (digits + thousands, NO "$") and dotless "cc".
    // The "$" is the static .cur span (set on rebuild); only the digits roll.
    function splitMoney(cents) {
      var s = fmtMoney(cents);
      if ((cents || 0) < 100) return { d: s.slice(1), c: "" };
      return { d: s.slice(1, -3), c: s.slice(-2) };
    }
    // Roll the main headline price. animate=false (first paint / no TPWRoll) shapes
    // instantly; animate=true rolls the dollars from the cached previous value.
    function rollMainPrice(cents, animate) {
      var priceEl = $("[data-price]"); if (!priceEl) return;
      // DEF-01: expose the TRUE selected-variant price in cents (integer) so the delivery
      // board reads it directly instead of parsing the dotless "Engraved Figure" text
      // ("$29.95" renders as textContent "$2995" -> false >$150 free-delivery). Updated on
      // first paint AND every variant change (this fn runs from updatePrice()).
      priceEl.setAttribute("data-price-cents", cents || 0);
      var parts = splitMoney(cents);
      var dEl = priceEl.querySelector("[data-price-d]");
      var cEl = priceEl.querySelector(".cents");
      var curEl = priceEl.querySelector(".cur");
      // Ensure the structure exists (.cur mark + dollars run; sub-$100 has no .cents);
      // rebuild if any piece is missing or the cents presence flipped.
      if (!dEl || !curEl || (parts.c && !cEl) || (!parts.c && cEl)) {
        priceEl.innerHTML = priceHtml(cents);
        dEl = priceEl.querySelector("[data-price-d]");
        cEl = priceEl.querySelector(".cents");
      }
      if (cEl) cEl.textContent = parts.c;   // cents committed instantly, no roll
      if (!dEl) return;
      // Price ROLL re-enabled (founder 2026-06-26) now that tpw-roll uses 'forwards'
      // fill — the reel always shows the correct target even if it can't animate, so
      // no more stuck-spin garbage. On first paint (animate=false) we set instantly;
      // on variant change we roll the dollars from the cached previous value.
      // M3 (founder 2026-06-27): the variant price now COUNT-TWEENS (Option B) to
      // match the cart, instead of the odometer reel. Plain text (no reels) -> also
      // sidesteps the old large-font reel-garbage saga.
      if (!_hasRoll()) { dEl.textContent = parts.d; _lastDollars = parts.d; return; }
      if (!animate || _lastDollars == null) {
        dEl.textContent = parts.d;
      } else if (window.TPWRoll.tween) {
        window.TPWRoll.tween(dEl, _lastDollars, parts.d);
      } else {
        dEl.textContent = parts.d;
      }
      _lastDollars = parts.d;
    }
    // Roll the ATC "Total" (flat money string). Used on variant change AND qty step.
    function rollAtcTotal(cents, animate) {
      var el = $("[data-atc-price]"); if (!el) return;
      // Add-ons in the box (17 Oct): ticked "Add to your setup" extras (qty 1 each) join the line total on the button.
      var t = fmtMoney(cents + extrasCents());
      if (!_hasRoll()) { el.textContent = t; _lastTotal = t; return; }
      if (!animate || _lastTotal == null) {
        el.textContent = t;
      } else if (window.TPWRoll.tween) {
        window.TPWRoll.tween(el, _lastTotal, t);   // count-tween (match cart)
      } else {
        el.textContent = t;
      }
      _lastTotal = t;
    }
    // F-18 MVP: count-tween the BNPL per-payment amount on variant change (shares the
    // shared TPWRoll.tween with the price + cart). animate=false (first paint) sets
    // instantly; animate=true counts from the cached previous value.
    function rollBnpl(cents, animate) {
      var t = fmtMoney(cents);
      // timeline node figures live inside the collapsed <details> panel -> set instantly
      var nodes = document.querySelectorAll("[data-bnpl-p]");
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = t;
      var el = document.querySelector("[data-bnpl-amt]"); if (!el) return;
      if (!_hasRoll()) { el.textContent = t; _lastBnpl = t; return; }
      if (!animate || _lastBnpl == null) {
        el.textContent = t;
      } else if (window.TPWRoll.tween) {
        window.TPWRoll.tween(el, _lastBnpl, t);   // only the summary figure tweens
      } else {
        el.textContent = t;
      }
      _lastBnpl = t;
    }
    // BNPL timeline fortnight dates (today + 2/4/6 weeks). No-JS falls back to the
    // server-rendered "2 wks / 4 wks / 6 wks" labels.
    var _BNPL_MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    function fillBnplDates() {
      var ds = document.querySelectorAll("[data-bnpl-d]");
      for (var i = 0; i < ds.length; i++) {
        var d = new Date(); d.setDate(d.getDate() + (i + 1) * 14);
        ds[i].textContent = d.getDate() + " " + _BNPL_MON[d.getMonth()];
      }
    }

    /* ============================================================
       GALLERY (clean cross-fade stack: swipe + thumbs + keyboard, looping)
       ============================================================ */
    function pad(n) { return String(n).padStart(2, "0"); }

    /* ---- image preloading so switching never lags ----
       For the inline gallery: when a frame becomes active, ensure the NEXT and
       PREVIOUS frame's <img> is fetched/decoded so the following swipe or thumb
       tap is instant. The first three inline images are already loading="eager" in markup.
       For the lightbox: preload the adjacent high-res zoom sources (data-zoom-src). */
    var _zoomCache = {}; // index -> Image (keeps refs alive so the browser caches)
    // Flash K11 (lane P, 2026-10-04): non-hero slides ship with data-src/data-srcset only (the deck stacks them in the
    // hero's box, so native lazy loading fetched all 22 at load). hydrateInline() moves the attributes onto the <img>
    // the first time a frame is about to show; called for the goFrame target and, via preloadInline, its neighbours.
    function hydrateInline(i) {
      if (i < 0 || i >= nImages) return;
      var img = imgs[i];
      if (!img || img.tagName !== "IMG" || !img.hasAttribute("data-src")) return;
      var ss = img.getAttribute("data-srcset");
      if (ss) { img.setAttribute("srcset", ss); img.removeAttribute("data-srcset"); }
      img.setAttribute("src", img.getAttribute("data-src"));
      img.removeAttribute("data-src");
    }
    function preloadInline(i) {
      if (i < 0 || i >= nImages) return;
      var img = imgs[i];
      if (!img || img.tagName !== "IMG") return;
      // Flash F5: a neighbour warmed before the page has loaded must not compete with the hero (the LCP) for the pipe.
      if (img.hasAttribute("data-src") && document.readyState !== "complete") img.setAttribute("fetchpriority", "low");
      hydrateInline(i);
      if (img.getAttribute("loading") === "lazy") img.setAttribute("loading", "eager");
      if (typeof img.decode === "function" && img.complete === false) { try { img.decode().catch(function () {}); } catch (e) {} }
    }
    function preloadAdjacentInline(i) {
      preloadInline(i + 1);
      preloadInline(i - 1);
    }
    function preloadZoom(i) {
      if (i < 0 || i >= nImages || _zoomCache[i]) return;
      var img = imgs[i];
      if (!img || img.tagName !== "IMG") return;
      var src = img.getAttribute("data-zoom-src") || img.currentSrc || img.src;
      if (!src) return;
      var pre = new Image();
      pre.src = src;
      _zoomCache[i] = pre;
    }
    function preloadAdjacentZoom(i) {
      preloadZoom(i);
      preloadZoom(i + 1);
      preloadZoom(i - 1);
    }
    // DEF-A01: keep the active thumb visible by moving ONLY the strip's own
    // scrollLeft. The old scrollIntoView({block:'nearest'}) here also scrolled
    // the PAGE ~65px at gallery init whenever the strip sat near the fold,
    // half-condensing the header over the breadcrumb on every PDP load.
    function revealThumb(t) {
      if (!t || !strip) return;
      var sr = strip.getBoundingClientRect(), tr = t.getBoundingClientRect();
      var target = strip.scrollLeft;
      if (tr.left < sr.left) target += tr.left - sr.left;
      else if (tr.right > sr.right) target += tr.right - sr.right;
      else return; // already fully in view -> no scroll (covers first paint)
      if (!REDUCED && strip.scrollTo) strip.scrollTo({ left: target, behavior: "smooth" });
      else strip.scrollLeft = target;
    }
    function updateMeta() {
      // Directional slide+fade (founder pick 2026-07-04). navDir is set by goFrame:
      // +1/-1 = sequential nav (slide in from that side), 0 = fade only (hero paint,
      // variant swap, or reduced motion). We drive the incoming/outgoing translateX
      // inline and let the CSS .slide/.slide.on transitions carry them (+ opacity) to rest.
      var prevOn = null;
      for (var pi = 0; pi < slides.length; pi++) { if (slides[pi].classList.contains("on")) { prevOn = slides[pi]; break; } }
      var target = slides[state.frame];
      var doSlide = !REDUCED && navDir !== 0 && target && prevOn && prevOn !== target;
      slides.forEach(function (s, i) {
        var on = i === state.frame;
        if (on) s.removeAttribute("aria-hidden"); else s.setAttribute("aria-hidden", "true");
        if (doSlide && (s === target || s === prevOn)) return; // handled below
        s.classList.toggle("on", on);
        s.style.transform = ""; s.style.transition = "";       // clear any leftover inline slide
      });
      if (doSlide) {
        var d = navDir;
        prevOn.style.zIndex = "1";                              // outgoing under incoming
        prevOn.classList.remove("on");                          // outgoing: fade + slide out
        prevOn.style.transform = "translateX(" + (-d * 30) + "px)";
        var po = prevOn; setTimeout(function () { po.style.transform = ""; po.style.transition = ""; po.style.zIndex = ""; }, 420);
        target.style.zIndex = "3";                              // incoming on top
        target.style.transition = "none";                       // prep off-side, opacity still 0
        target.style.transform = "translateX(" + (d * 30) + "px)";
        void target.offsetWidth;                                // commit start with no transition
        target.style.transition = "";                           // restore CSS .slide.on transition
        target.classList.add("on");                             // opacity 0 -> 1 (CSS)
        target.style.transform = "translateX(0)";               // 30px -> 0 (transform transition)
        var tg = target; setTimeout(function () { tg.style.transform = ""; tg.style.transition = ""; tg.style.zIndex = ""; }, 420);
      }
      navDir = 0;                                               // consume: default back to fade
      // match thumbs by data-index. The strip is now in de-duped, hero-first SEQUENCE order
      // (DOM order == frame order), so data-index === slide frame index.
      var active = null;
      thumbs.forEach(function (t) {
        var on = (+t.getAttribute("data-index")) === state.frame;
        t.setAttribute("aria-current", on ? "true" : "false");
        if (on) active = t;
      });
      revealThumb(active);
      // G1: frame counter. nImages = the de-duped frame count -> counter opens "01 / NN".
      if (gCount) { var n = state.frame + 1; gCount.textContent = (n < 10 ? "0" : "") + n + " / " + (nImages < 10 ? "0" : "") + nImages; }
      // G1: position rail (P-014) — PERMANENT, capped at gTicks.length (<=12). Map the current
      // frame proportionally to the rendered ticks so the active tick tracks position even when
      // there are more frames than ticks. Frame 0 -> tick 0 (matches the Liquid first paint).
      if (gTicks && gTicks.length) {
        var activeTick = 0;
        if (nImages > 1 && gTicks.length > 1) {
          activeTick = Math.round((state.frame / (nImages - 1)) * (gTicks.length - 1));
        }
        for (var k = 0; k < gTicks.length; k++) gTicks[k].classList.toggle("on", k === activeTick);
      }
    }
    // goFrame keeps the (i, animate) signature for callers; the opacity cross-fade
    // is CSS-driven, so toggling the active slide IS the transition.
    // FI-07: navigation LOOPS — wraps modulo from both ends (last -> first -> last),
    // so "next" on the final frame never dead-ends (founder-requested loop).
    function goFrame(i, dir) {
      if (nImages < 1) return;
      var prev = state.frame;
      state.frame = ((i % nImages) + nImages) % nImages;
      // direction for the slide+fade: explicit hint from sequential callers wins;
      // dir===0 forces a pure fade (variant swap); thumb jumps derive the sign.
      if (dir === 0 || state.frame === prev) navDir = 0;
      else if (typeof dir === "number") navDir = dir < 0 ? -1 : 1;
      else navDir = state.frame > prev ? 1 : -1;
      hydrateInline(state.frame); // K11: the incoming frame must have its source before it paints
      updateMeta();
      preloadAdjacentInline(state.frame);
      // keep zoom navigation instant if the lightbox is open
      if (lb && !lb.hidden && lb.classList.contains("open")) preloadAdjacentZoom(state.frame);
    }

    thumbs.forEach(function (t) {
      t.addEventListener("click", function () { goFrame(+t.getAttribute("data-index")); });
    });

    function setupStage() {
      if (!stage) return;

      // keyboard: arrows browse frames (looping), Enter/Space opens the lightbox
      stage.addEventListener("keydown", function (e) {
        if (e.target !== stage) return; // ignore keys when focus is on a child control (zoom)
        if (e.key === "ArrowRight") { e.preventDefault(); goFrame(state.frame + 1, 1); }
        else if (e.key === "ArrowLeft") { e.preventDefault(); goFrame(state.frame - 1, -1); }
        else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLightbox(); }
      });

      // click anywhere on the image opens the lightbox (zoom)
      stage.addEventListener("click", function () { if (nImages > 0) openLightbox(); });
      var zoomBtn = $("[data-zoom-open]");
      if (zoomBtn) zoomBtn.addEventListener("click", function (e) { e.stopPropagation(); openLightbox(); });

      // gallery prev/next arrows (restored 2026-07-04) — stopPropagation so the arrow
      // tap navigates instead of bubbling to the stage's click-to-zoom.
      if (gPrev) gPrev.addEventListener("click", function (e) { e.stopPropagation(); goFrame(state.frame - 1, -1); });
      if (gNext) gNext.addEventListener("click", function (e) { e.stopPropagation(); goFrame(state.frame + 1, 1); });
    }

    /* ---- FI-06: horizontal swipe on a gallery stage (touch + mouse drag) ----
       Pointer-based: >=40px horizontal travel with horizontal dominance drives
       prev/next (looping via goFrame). The stage keeps touch-action:pan-y, so a
       vertical drag stays a page scroll (the browser fires pointercancel and we
       abort). A sub-threshold tap falls through untouched, so click-to-zoom
       still works; a real swipe swallows its trailing click in the capture
       phase (same pattern as the strip drag guard below). Reduced motion:
       gesture still navigates; the CSS reduced-motion block makes it instant. */
    function setupSwipe(el, go) {
      if (!el) return;
      var pid = null, sx = 0, sy = 0, dx = 0, dy = 0, swiped = false, captured = false;
      el.addEventListener("pointerdown", function (e) {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        // GAL-01 fix: never start a swipe on an interactive control (prev/next arrows,
        // zoom, lightbox close). Capturing the pointer here would retarget that control's
        // click to the stage — reopening the "arrow only zooms" bug on desktop mouse.
        if (e.target.closest && e.target.closest("button, a, [data-zoom-open]")) { pid = null; return; }
        pid = e.pointerId; sx = e.clientX; sy = e.clientY; dx = 0; dy = 0; swiped = false; captured = false;
      });
      el.addEventListener("pointermove", function (e) {
        if (e.pointerId !== pid) return;
        dx = e.clientX - sx; dy = e.clientY - sy;
        // GAL-01 fix: capture ONLY once a real horizontal drag starts, so a plain click
        // is never retargeted away from its true target. Capturing keeps moves/up coming
        // even if the pointer leaves the stage mid-swipe.
        if (!captured && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          captured = true;
          if (el.setPointerCapture) { try { el.setPointerCapture(pid); } catch (err) {} }
        }
      });
      el.addEventListener("pointerup", function (e) {
        if (e.pointerId !== pid) return;
        pid = null;
        if (nImages > 1 && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          go(dx < 0 ? 1 : -1);
        }
      });
      el.addEventListener("pointercancel", function (e) { if (e.pointerId === pid) pid = null; });
      el.addEventListener("click", function (e) {
        if (swiped) { swiped = false; e.stopPropagation(); e.preventDefault(); }
      }, true);
    }

    /* ---- thumb strip drag (horizontal scroll convenience only) ---- */
    function setupStripDrag() {
      if (!strip) return;
      var down = false, sx = 0, sl = 0, moved = false;
      strip.addEventListener("pointerdown", function (e) { down = true; moved = false; sx = e.clientX; sl = strip.scrollLeft; strip.classList.add("grabbing"); });
      strip.addEventListener("pointermove", function (e) { if (!down) return; var dx = e.clientX - sx; if (Math.abs(dx) > 4) moved = true; strip.scrollLeft = sl - dx; });
      var up = function () { down = false; strip.classList.remove("grabbing"); };
      strip.addEventListener("pointerup", up);
      strip.addEventListener("pointerleave", up);
      strip.addEventListener("click", function (e) { if (moved) { e.stopPropagation(); e.preventDefault(); } }, true);
    }

    /* ============================================================
       LIGHTBOX
       ============================================================ */
    var lb = $("[data-lightbox]"), lbStage = $("[data-lb-stage]"), lastFocus = null;
    function lbImgHtml(i) {
      var img = imgs[i];
      if (!img) return "";
      var src = img.getAttribute("data-zoom-src") || img.currentSrc || img.src;
      if (!src) return img.outerHTML; // placeholder svg case
      return '<img src="' + src + '" alt="' + (img.getAttribute("alt") || "") + '">';
    }
    function lbMeta() {
      var m = $("[data-lb-meta]");
      if (m) m.textContent = pad(state.frame + 1) + " / " + pad(nImages);
    }
    // swap the lightbox image and run the lux fade-in (opacity + tiny scale settle)
    function lbRender() {
      if (!lbStage) return;
      lbStage.innerHTML = lbImgHtml(state.frame);
      var im = lbStage.querySelector("img");
      if (!im) return;
      if (REDUCED) { im.classList.add("lb-in"); return; }
      // force a reflow so the from-state applies, then add the class on the next frame
      void im.offsetWidth;
      requestAnimationFrame(function () { im.classList.add("lb-in"); });
    }
    // hide the rest of the page from AT / focus while the modal is open
    function setBgInert(on) {
      [$(".pdpv9-crumb"), $(".pdpv9-main")].forEach(function (el) {
        if (!el) return;
        if (on) { el.setAttribute("inert", ""); el.setAttribute("aria-hidden", "true"); }
        else { el.removeAttribute("inert"); el.removeAttribute("aria-hidden"); }
      });
    }
    function openLightbox() {
      if (!lb || nImages < 1) return;
      // ensure the current zoom image (and its neighbours) are fetched up front
      preloadAdjacentZoom(state.frame);
      lb.hidden = false;
      lbRender();
      lbMeta();
      // toggle nav arrows for single-image
      var prev = $("[data-lb-prev]"), next = $("[data-lb-next]");
      if (prev) prev.hidden = nImages <= 1;
      if (next) next.hidden = nImages <= 1;
      requestAnimationFrame(function () { lb.classList.add("open"); });
      lastFocus = document.activeElement;
      var c = $("[data-lb-close]"); if (c) c.focus();
      document.body.style.overflow = "hidden";
      setBgInert(true);
    }
    function lbGo(dir) {
      if (nImages <= 1) return;
      goFrame(state.frame + dir, dir);
      lbRender();
      lbMeta();
      // keep the next hop instant
      preloadAdjacentZoom(state.frame);
    }
    function closeLightbox() {
      if (!lb) return;
      lb.classList.remove("open");
      setTimeout(function () { lb.hidden = true; }, 400);
      document.body.style.overflow = "";
      setBgInert(false);
      (lastFocus || stage || document.body).focus && (lastFocus || stage || document.body).focus();
    }
    function setupLightbox() {
      if (!lb) return;
      var c = $("[data-lb-close]"), p = $("[data-lb-prev]"), n = $("[data-lb-next]");
      if (c) c.addEventListener("click", closeLightbox);
      if (p) p.addEventListener("click", function () { lbGo(-1); });
      if (n) n.addEventListener("click", function () { lbGo(1); });
      lb.addEventListener("click", function (e) { if (e.target === lb) closeLightbox(); });
      document.addEventListener("keydown", function (e) {
        if (lb.hidden || !lb.classList.contains("open")) return;
        if (e.key === "Escape") { closeLightbox(); return; }
        if (e.key === "ArrowRight") { lbGo(1); return; }
        if (e.key === "ArrowLeft") { lbGo(-1); return; }
        if (e.key === "Tab") {
          var f = [c, p, n].filter(function (x) { return x && !x.hidden; });
          if (!f.length) return;
          var idx = f.indexOf(document.activeElement);
          e.preventDefault();
          var nx = e.shiftKey ? (idx <= 0 ? f.length - 1 : idx - 1) : ((idx + 1) % f.length);
          f[nx].focus();
        }
      });
    }

    /* ============================================================
       VARIANT SELECTOR
       ============================================================ */
    var chips = $$("[data-chip]");
    var optSelect = $("[data-optselect]"); // list-picker <select> (>10 values tier)
    var optblock = $("[data-optblock]");
    function currentVariant() { return variants[state.selVar] || null; }

    // Sold-out values are hidden, so chips no longer map 1:1 to the variants array by
    // DOM position. Each chip carries data-index = its index in the FULL variants blob.
    function chipVarIndex(c) { return parseInt(c.getAttribute("data-index"), 10); }
    function chipForIndex(i) {
      for (var k = 0; k < chips.length; k++) { if (chipVarIndex(chips[k]) === i) return chips[k]; }
      return null;
    }
    // theme the selector accent (--variant-color) from the chosen value's colour;
    // empty -> remove so CSS falls back to gold. Keeps gold rationing intact.
    function applyVariantColor(c) {
      if (!optblock) return;
      c = (c || "").trim();
      if (c) optblock.style.setProperty("--variant-color", c);
      else optblock.style.removeProperty("--variant-color");
    }

    function selectVariant(i) {
      if (i === state.selVar) return;
      if (!variants[i]) return;
      state.selVar = i;
      chips.forEach(function (c) {
        var on = chipVarIndex(c) === i;
        c.setAttribute("aria-checked", on ? "true" : "false");
        c.tabIndex = on ? 0 : -1;
      });
      // colour theming from chips (list tier handled in its own change listener)
      if (chips.length) {
        var sc = chipForIndex(i);
        applyVariantColor(sc ? sc.style.getPropertyValue("--swatch-c") : "");
      }
      // keep the list-picker <select> in sync (when it is the active control)
      if (optSelect && variants[i] && String(optSelect.value) !== String(variants[i].id)) {
        optSelect.value = variants[i].id;
      }
      var v = variants[i];
      // mirror to the hidden form id input (drives real add-to-cart)
      if (variantInput) {
        variantInput.value = v.id;
        if (v.available) variantInput.removeAttribute("disabled"); else variantInput.setAttribute("disabled", "disabled");
      }
      // DEF-03: reflect the selected variant in the address bar so it can be shared /
      // bookmarked. replaceState (not pushState) so the browser Back button isn't spammed
      // with one entry per swatch. Preserve the existing path; only rewrite ?variant=.
      // Deep-link selection on load is already handled server-side by Shopify
      // (selected_or_first_available_variant), so a direct ?variant=<id> URL still opens
      // on that variant. Guarded so an unusual environment can never throw.
      try {
        if (window.history && history.replaceState) {
          history.replaceState({}, "", location.pathname + "?variant=" + v.id);
        }
      } catch (eh) {}
      var sel = $("[data-optsel]"); if (sel) sel.textContent = variantTitle(v);
      // CRO-02 desktop strip variant readout (document-level: the bar is portaled)
      var dVar = document.querySelector("[data-d-var]"); if (dVar) dVar.textContent = variantTitle(v);
      var nudge = $("[data-soldout-nudge]"); if (nudge) nudge.classList.toggle("show", !v.available);
      updateVariantImage(v);
      updatePrice(true);
      var vstat = $("[data-variant-status]");
      if (vstat) vstat.textContent = variantTitle(v) + ", " + fmtMoney(v.price) + (v.available ? "" : ", sold out");
    }

    function variantTitle(v) {
      // option NAME only (no price), matching the prototype readout
      return (v.options && v.options.length === 1) ? v.options[0] : (v.title || "");
    }

    function updateVariantImage(v) {
      if (!v) return;
      // switch to the variant's featured image. Match by MEDIA ID against the rendered
      // slides (robust wherever the image sits); fall back to media position. The
      // active-slide toggle IS the opacity cross-fade (CSS-driven).
      var fid = (v.featured_media && v.featured_media.id) || (v.featured_image && v.featured_image.id);
      var idx = null;
      if (fid != null && mediaIndexById[fid] != null) {
        idx = mediaIndexById[fid];
      } else {
        var pos = (v.featured_media && v.featured_media.position) || (v.featured_image && v.featured_image.position);
        if (pos && pos >= 1 && pos <= nImages) idx = pos - 1;
      }
      if (idx != null && idx !== state.frame) goFrame(idx, 0); // variant swap = calm fade, no slide
    }

    // roving focus moves by DOM position among the rendered (available) chips;
    // selection targets the chip's variant index (data-index into the full blob).
    function onChipKey(e, chip) {
      var n = chips.length;
      var pos = chips.indexOf(chip);
      var nextPos = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); nextPos = (pos + 1) % n; }
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); nextPos = (pos - 1 + n) % n; }
      else if (e.key === " " || e.key === "Enter") { e.preventDefault(); var vi = chipVarIndex(chip); if (variants[vi] && variants[vi].available) selectVariant(vi); return; }
      if (nextPos === null) return;
      chips.forEach(function (c, idx) { c.tabIndex = idx === nextPos ? 0 : -1; });
      chips[nextPos].focus();
      var nvi = chipVarIndex(chips[nextPos]);
      if (variants[nvi] && variants[nvi].available) selectVariant(nvi);
    }

    chips.forEach(function (c) {
      var vi = chipVarIndex(c);
      c.addEventListener("click", function () { if (variants[vi] && variants[vi].available) selectVariant(vi); });
      c.addEventListener("keydown", function (e) { onChipKey(e, c); });
    });

    // list-picker <select> (>10 values): drives the same variant pipeline.
    // Native <select> is keyboard + screen-reader correct. Sold-out options are not
    // rendered. We map the chosen id back to its variant index, then theme the accent.
    if (optSelect) {
      optSelect.addEventListener("change", function () {
        var id = parseInt(optSelect.value, 10);
        for (var k = 0; k < variants.length; k++) {
          if (variants[k].id === id) { selectVariant(k); break; }
        }
        var opt = optSelect.options[optSelect.selectedIndex];
        applyVariantColor(opt ? opt.getAttribute("data-swatch-c") : "");
      });
    }

    /* ---- price / save / atc / mobile (synchronous text first, zero shift) ---- */
    function animatePrice(el, toCents) {
      // Correctness over flourish: commit the final price instantly and reliably.
      // (A count-up animation can stick on a stale value if rAF is paused/throttled
      // mid-tween, which would show a wrong price for the selected variant.)
      if (!el) return;
      el.textContent = fmtMoney(toCents);
    }

    function updatePrice(animate) {
      var v = currentVariant();
      if (!v) return;
      clampQtyToStock(); // a variant may hold less stock than the last; keep qty <= on hand
      var priceEl = $("[data-price]"), rrprow = $("[data-rrprow]");
      // F-24: roll the headline price (dollars reel) instead of a hard innerHTML swap.
      // Same value priceHtml() would render; cents stay committed instantly inside.
      if (priceEl) rollMainPrice(v.price, animate);   // PR1: cents de-emphasised
      // the Add to cart button shows the LINE total (qty x unit). On variant change we
      // roll it to the new line total; qty steps roll via bumpAtcPrice(). The reel
      // commits the exact value up-front, so a starved frame can never show a stale price.
      rollAtcTotal(v.price * currentQty(), animate);
      var mPrice = document.querySelector("[data-m-price]"); if (mPrice) mPrice.textContent = fmtMoney(v.price);
      // CRO-02 desktop strip price (document-level lookup: the bar is portaled to a
      // body host by setupDesktopBar, same as the mbar bits above/below)
      var dPrice = document.querySelector("[data-d-price]"); if (dPrice) dPrice.textContent = fmtMoney(v.price);

      var mCmp = document.querySelector("[data-m-compare]");
      var saleFlag = $("[data-sale-flag]");   // G3: gallery "Save $X" flag
      var onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (onSale) {
        var saveCents = v.compare_at_price - v.price;
        // "Engraved Figure": strikethrough WAS price + a single gold "$X off" token.
        var rrpWas = $("[data-rrp-was]"); if (rrpWas) rrpWas.textContent = fmtMoney(v.compare_at_price);
        var rrpOff = $("[data-rrp-off]"); if (rrpOff) rrpOff.textContent = fmtMoney(saveCents);
        if (rrprow) rrprow.hidden = false;
        if (priceEl) priceEl.classList.add("is-sale");
        if (mCmp) { mCmp.textContent = fmtMoney(v.compare_at_price); mCmp.setAttribute("data-empty", "false"); }
        var dCmpOn = document.querySelector("[data-d-compare]");
        if (dCmpOn) { dCmpOn.textContent = fmtMoney(v.compare_at_price); dCmpOn.setAttribute("data-empty", "false"); }
        if (saleFlag) { var sfa = saleFlag.querySelector("[data-sale-amt]"); if (sfa) sfa.textContent = fmtMoney(saveCents); saleFlag.hidden = false; }
      } else {
        if (rrprow) rrprow.hidden = true;
        if (priceEl) priceEl.classList.remove("is-sale");
        if (mCmp) mCmp.setAttribute("data-empty", "true");
        var dCmpOff = document.querySelector("[data-d-compare]");
        if (dCmpOff) dCmpOff.setAttribute("data-empty", "true");
        if (saleFlag) saleFlag.hidden = true;
      }

      // F-18 (founder 2026-06-27): the BNPL line shows on ALL products (pay-in-4 works at
      // any price, so no $ floor). Per-payment = price/4; count-tweens on variant change.
      // (The "Free delivery over $150" line was removed; the Journey Rail owns it now.)
      var bnplPlate = $("[data-bnpl]");
      if (bnplPlate) {
        bnplPlate.hidden = false;
        bnplPlate.classList.remove("is-hidden");
        // BNPL reflects what they're about to add = unit price x quantity, /4 (founder 2026-06-27)
        rollBnpl(Math.round(v.price * currentQty() / 4), animate);
      }

      // ATC + mobile sold-out state
      var atc = $("[data-atc]"), atcLabel = $("[data-atc-label]"), mAtc = $("[data-m-atc]");
      if (atc) {
        if (!v.available) { atc.disabled = true; if (atcLabel && !atc.classList.contains("added")) atcLabel.textContent = "Sold out"; }
        else { atc.disabled = false; if (atcLabel && !atc.classList.contains("added") && !atc.classList.contains("adding")) atcLabel.textContent = "Add to cart"; }
      }
      // X-6: the bar labels live in their own spans ([data-m-atc-label] / [data-d-atc-label]) so TPWLoading's
      // layers on the button survive a label write. Never rewrite while the beam owns the button (__busy).
      if (mAtc) { mAtc.disabled = !v.available; var mLbl = mAtc.querySelector("[data-m-atc-label]"); if (mLbl && !mAtc.__busy) mLbl.textContent = v.available ? "Add to cart" : "Sold out"; }
      var mVar = document.querySelector("[data-m-var]"); if (mVar) mVar.textContent = ", " + variantTitle(v);
      // CRO-02 desktop strip ATC: disabled state follows the selected variant.
      var dAtc = document.querySelector("[data-d-atc]");
      if (dAtc) { dAtc.disabled = !v.available; var dLbl = dAtc.querySelector("[data-d-atc-label]"); if (dLbl && !dAtc.__busy) dLbl.textContent = v.available ? "Add to cart" : "Sold out"; }

      // Founder 17 Oct (Release 4): no stock counts on the PDP (the Dial's "X left" cell and the sticky bar's "N left" are gone).
    }
    function currentQty() {
      var q = $("[data-qty-input]");
      return q ? Math.max(1, Math.min(99, parseInt(q.value, 10) || 1)) : 1;
    }
    // hard cap = on-hand stock for the selected variant (null/untracked/oversell -> 99)
    function currentMax() {
      var v = currentVariant(); if (!v) return 99;
      var info = invMap[v.id];
      var m = info && typeof info.m === "number" ? info.m : null;
      return m == null ? 99 : Math.max(1, Math.min(99, m));
    }
    // never let the chosen quantity exceed stock; keep the up control disabled at the cap.
    // Runs on init and on every variant change (a variant may have less stock than the last).
    function clampQtyToStock() {
      var inp = $("[data-qty-input]"); if (!inp) return;
      var max = currentMax();
      var cur = Math.max(1, Math.min(99, parseInt(inp.value, 10) || 1));
      var nx = Math.min(cur, max);
      if (nx !== cur) {
        inp.value = nx;
        var disp = $("[data-qty-display]"); if (disp) disp.textContent = nx;
      }
      var up = $('[data-qty-step="1"]'); if (up) up.disabled = nx >= max;
      // F-01: when only the last unit is available the steppers do nothing — hide them
      // (a single Add to cart then adds the one unit). Runs on init + variant change.
      var dial = $("[data-atcdial]"); if (dial) dial.classList.toggle("atcdial--single", max === 1);
    }
    // Set the Add-to-cart price to an exact value INSTANTLY (correctness: a numeric
    // count-up can freeze on a stale value if rAF/timers are throttled), then a quick
    // scale "pop" for life. The gold numeral roll is the component's signature effect.
    function bumpAtcPrice(toCents) {
      var el = $("[data-atc-price]"); if (!el) return;
      // F-24: roll the Total to its new exact value (correctness: the reel commits the
      // target up-front; no count-up that could freeze on a stale figure).
      rollAtcTotal(toCents, true);
      rollBnpl(Math.round(toCents / 4), true);   // BNPL tracks the line total (qty x unit) /4

      if (!REDUCED && el.animate) {
        el.animate([{ transform: "scale(1.14)" }, { transform: "scale(1)" }], { duration: 300, easing: "cubic-bezier(.16,1,.3,1)" });
      }
    }
    // THE DIAL: up/down buttons drive a hidden quantity input + the gold numeral (which
    // rolls) + the button price (which counts up to qty x unit). No manual typing.
    function setupQty() {
      var inp = $("[data-qty-input]"); if (!inp) return;
      var disp = $("[data-qty-display]");
      $$("[data-qty-step]").forEach(function (b) {
        b.addEventListener("click", function () {
          var step = parseInt(b.getAttribute("data-qty-step"), 10) || 0;
          var cur = Math.max(1, Math.min(99, parseInt(inp.value, 10) || 1));
          var max = currentMax();
          var nx = Math.max(1, Math.min(max, cur + step));
          if (nx === cur) return; // already at 1 or at the stock cap
          inp.value = nx;
          if (disp) {
            disp.textContent = nx;
            if (!REDUCED && disp.animate) {
              // M3 (founder pick A): a GENTLE pop + slide on the digit. The white
              // text-shadow GLOW was dropped (read gamer-y, not premium). One combined
              // transform keyframe so the slide + pop don't fight.
              disp.animate([
                { transform: "translateY(" + (step > 0 ? 10 : -10) + "px) scale(1.08)", opacity: 0 },
                { transform: "translateY(0) scale(1)", opacity: 1 }
              ], { duration: 260, easing: "cubic-bezier(.16,1,.3,1)" });
            }
          }
          var v = currentVariant();
          if (v) bumpAtcPrice(v.price * nx);
          var up = $('[data-qty-step="1"]'); if (up) up.disabled = nx >= max;
        });
      });
    }

    /* ============================================================
       ADD TO CART (real AJAX; native form is the no-JS fallback)
       ============================================================ */
    var addUrl = root.getAttribute("data-cart-add-url") || "/cart/add";
    // product type -> personal "[thing] is yours!" success line (P1 build #1
    // founder change 2). Resolved once; safe default inside ownLine().
    var pType = root.getAttribute("data-tpw-type") || "";
    function ownLine() {
      return (window.TPWLoading && TPWLoading.ownLine) ? TPWLoading.ownLine(pType) : "Added";
    }
    /* Orion (Argus S-01/S-03, 2026-10-03): the refusal Shopify returns with a 422 ("The maximum quantity of
       this item is already in your cart.") was written ONLY to the sr-only live region, so a sighted customer
       saw the button snap back to "Add to cart" and nothing else. One visible line under the control, cleared
       on the next attempt and on success. The live region keeps announcing it. */
    /* Orion pass 2 (Argus re-verify R-01/R-02): the line under the form is below the fold when the shopper used
       the phone's sticky bar or the desktop strip (y 986 on an 844 screen), and those two paths left the live
       region reading "Adding to cart". showRefusal(msg, barSel) now (1) writes the refusal into the ONE live region
       on every path, (2) shows it under the in-flow form as before, and (3) when the tap came from a docked bar,
       ALSO shows it on that bar ([data-bar-refusal] inside .mbar / .dbar), where the tap happened. clearRefusal()
       empties all of them; a success then writes its own line into the live region. The bars are portaled to
       body-level hosts, so they are looked up on the document. */
    var refusalEl = $("[data-atc-refusal]");
    function barRefusals() { return document.querySelectorAll("[data-bar-refusal]"); }
    function showRefusal(msg, barSel) {
      var status = $("[data-atc-status]");
      if (status) status.textContent = msg;
      if (refusalEl) { refusalEl.textContent = msg; refusalEl.hidden = false; }
      if (barSel) {
        var bar = document.querySelector(barSel);
        var slot = bar && bar.querySelector("[data-bar-refusal]");
        if (slot) { slot.textContent = msg; slot.hidden = false; }
      }
    }
    function clearRefusal() {
      if (refusalEl) { refusalEl.textContent = ""; refusalEl.hidden = true; }
      var slots = barRefusals();
      for (var i = 0; i < slots.length; i++) { slots[i].textContent = ""; slots[i].hidden = true; }
    }
    // Hephaestus R8 (R7-16, Argus R7, 5 Oct 2026): ONLY a refusal the store wrote (a JSON description, flagged tpsServer
    // below) is shown verbatim. Before, ANY error's message reached the shopper: under a rate limit /cart/add.js answers
    // with an HTML challenge page and the line read "Unexpected token '<', "<!DOCTYPE "... is not valid JSON"; the 12 s
    // hang guard showed "signal is aborted without reason". Those now read the house line.
    function refusalText(err) {
      return (err && err.tpsServer && err.message) ? err.message : "Sorry, this could not be added to your cart.";
    }
    // Add-ons in the box (17 Oct): assets/tps-setup.js (box branch) sets root.__tpsExtras = { cents(), items(), done(r) }.
    function extrasCents() { var x = root.__tpsExtras; return (x && x.cents) ? x.cents() : 0; }
    function addToCartAjax(id, qty, extra, quiet) {
      clearRefusal();
      var dr = document.querySelector("cart-drawer");
      // extras first, main item LAST (same order as the old panel's add: the pipe is the top line in the drawer)
      var body = { items: (extra || []).map(function (x) { return { id: x.id, quantity: 1 }; }).concat(id ? [{ id: id, quantity: qty || 1 }] : []) };
      if (dr && typeof dr.getSectionsToRender === "function") {
        body.sections = dr.getSectionsToRender().map(function (s) { return s.id; }).join(",");
        body.sections_url = location.pathname;
      }
      window.__tpwCartHandled = Date.now();
      // Founder-reported (2026-07-03): on an in-app browser the add request can HANG
      // (never resolves, never rejects) and the button sticks on "Adding" forever.
      // 12s abort turns a hang into a normal rejection so the catch path resets the UI.
      var ac = (typeof AbortController !== "undefined") ? new AbortController() : null;
      if (ac) setTimeout(function () { ac.abort(); }, 12000);
      return fetch((addUrl.indexOf(".js") > -1 ? addUrl : "/cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
        signal: ac ? ac.signal : undefined
      })
        .then(function (r) {
          // R7-16: parse defensively; a non-JSON body (429 / challenge HTML) is a plain failure, never a thrown parse error
          return r.text().then(function (t) {
            var res = null;
            try { res = JSON.parse(t); } catch (e) { res = null; }
            // Shopify returns 422 (sold out / limit) — do NOT treat as success
            if (!r.ok || !res) {
              var err = new Error((res && res.description) || "add-failed");
              if (res && res.description) err.tpsServer = true;
              throw err;
            }
            return res;
          });
        })
        .then(function (res) {
          // R3A-12 (lane P, 2026-10-04): the drawer used to open ~2.6 s after the tap because this path IGNORED the
          // `sections` already in the add.js response and made a second /?sections= round trip (the comment that
          // justified it, "sections often absent", dates from 2026-06-26; the drawer's own rail + has rendered from
          // the add response since lane D with no stale case seen). Now: render + open from the response in the same
          // tick (the drawer's data-tpsd-art hold still gates a tobacco slip; untouched), and fall back to the
          // section-render fetch ONLY when the response carries no usable sections, so a missing payload can
          // never leave the cart stale.
          window.__tpwCartHandled = Date.now();
          var drNow = document.querySelector("cart-drawer");
          var secs = res && res.sections;
          var usable = false;
          if (secs && drNow && typeof drNow.renderContents === "function" && typeof drNow.getSectionsToRender === "function") {
            usable = drNow.getSectionsToRender().every(function (sc) { return typeof secs[sc.id] === "string" && secs[sc.id] !== ""; });
          }
          if (usable) { drNow.renderContents(res); }
          else { refreshCartUI(); }
          // Completion Engine hook: pdpv9 is a bespoke form that does NOT use the
          // theme's <product-form>, so it never emitted the standard variant:add.
          // Emit it here on success so tpw-completion-engine.js can offer the
          // completing item. Deliberately NO formElement in the detail → the legacy
          // Dawn / sticky-buy-bar "Added" listeners (which require formElement inside
          // .product .main-information) stay dormant (pdpv9 has its own added state);
          // only the completion engine, which needs just the variant id, reacts.
          if (!quiet) try {
            document.dispatchEvent(new CustomEvent("variant:add", {
              detail: { variant: { id: id }, quantity: qty || 1, source: "pdpv9" }
            }));
          } catch (e) {}
          return res;
        });
    }
    // Guaranteed cart-UI refresh: fetch the cart sections from the dedicated
    // section-render endpoint and apply via the drawer's own renderContents (which
    // updates #cart-icon-bubble + #CartDrawer and rolls the totals). Decoupled from
    // the add response so a missing res.sections can never leave the cart stale.
    function refreshCartUI() {
      var dr = document.querySelector("cart-drawer");
      var ids = (dr && typeof dr.getSectionsToRender === "function")
        ? dr.getSectionsToRender().map(function (s) { return s.id; }).join(",")
        : "cart-icon-bubble,cart-drawer";
      fetch("/?sections=" + encodeURIComponent(ids), { headers: { Accept: "application/json" } })
        .then(function (r) { if (!r.ok) throw new Error("sections " + r.status); return r.json(); }) /* R7-16: a 429 page is never parsed */
        .then(function (secs) {
          if (secs && dr && typeof dr.renderContents === "function") {
            dr.renderContents({ sections: secs });
          }
        })
        .catch(function () {});
    }

    /* P1 "the Beam": the WORKING/DONE motion now comes from the shared
       window.TPWLoading (assets/tpw-loading.{css,js}); this button keeps its
       OWN native .check tick (tick:false) + .lbl/price structure, so the
       loader only drives the white beam, the label text + aria/live state.
       Guarded: if the foundation hasn't loaded, fall back to the old classes. */
    function atcWorking(btn, label) {
      var status = $("[data-atc-status]");
      if (window.TPWLoading) {
        TPWLoading.start(btn, { shape: "wide", onColor: "dark", label: "Adding",
          labelEl: label, status: status, statusText: "Adding to cart" });
      } else {
        btn.classList.add("adding"); if (label) label.textContent = "Adding";
        if (status) status.textContent = "Adding to cart";
      }
    }
    function calmAdded(btn, label) {
      var status = $("[data-atc-status]");
      var v = currentVariant();
      var idle = (v && !v.available) ? "Sold out" : "Add to cart";
      var doneLine = ownLine();
      if (window.TPWLoading) {
        // tick:false — the button draws its OWN .check via .added (below)
        btn.classList.remove("adding"); btn.classList.add("added");
        TPWLoading.done(btn, { tick: false, label: doneLine, labelEl: label,
          status: status, statusText: doneLine, hold: 2200, idleLabel: idle });
        setTimeout(function () { btn.classList.remove("added"); }, 2200);
      } else {
        btn.classList.remove("adding"); btn.classList.add("added");
        if (label) label.textContent = doneLine;
        if (status) status.textContent = doneLine;
        setTimeout(function () {
          btn.classList.remove("added");
          if (label) label.textContent = idle;
        }, 2200);
      }
    }

    // Add-ons in the box: ONE call for the main item + ticked extras. Shopify refuses the whole call when any line fails (an
    // extra sold out mid-session), so on a refusal: the main item alone, then each extra alone; the result says which
    // extras did not add. A refusal of the main item alone is the normal refusal path. No extras = the old single add.
    function addWithExtras(id, qty, ex) {
      if (!ex.length) return addToCartAjax(id, qty).then(function () { return { ok: [], bad: [] }; });
      return addToCartAjax(id, qty, ex).then(function () { return { ok: ex, bad: [] }; }, function () {
        return addToCartAjax(id, qty).then(function () {
          var ok = [], bad = [], ch = Promise.resolve();
          ex.forEach(function (x) { ch = ch.then(function () { return addToCartAjax(0, 0, [x], true).then(function () { ok.push(x); }, function () { bad.push(x); }); }); });
          return ch.then(function () { return { ok: ok, bad: bad }; });
        });
      });
    }
    root.__tpsAtcRepaint = function () { var v = currentVariant(); if (v) rollAtcTotal(v.price * currentQty(), true); };
    var form = $(".pdpv9-form");
    var atcBtn = $("[data-atc]");
    if (form && atcBtn) {
      form.addEventListener("submit", function (e) {
        var v = currentVariant();
        if (v && !v.available) { e.preventDefault(); return; }
        if (!("fetch" in window)) return; // let the native form submit (no-JS / old browser)
        e.preventDefault();
        if (atcBtn.classList.contains("adding") || atcBtn.__tpwlBusy) return;
        var label = $("[data-atc-label]");
        atcWorking(atcBtn, label);
        var id = (v && v.id) || (variantInput && variantInput.value);
        var xs = root.__tpsExtras, ex = (xs && xs.items) ? xs.items() : [];
        addWithExtras(+id, currentQty(), ex)
          .then(function (r) {
            calmAdded(atcBtn, label);
            var msg = (xs && xs.done) ? xs.done(r) : "";
            if (msg) setTimeout(function () { showRefusal(msg); }, 60);
          })
          .catch(function (err) {
            var cv = currentVariant();
            var idle = (cv && !cv.available) ? "Sold out" : "Add to cart";
            if (window.TPWLoading) { TPWLoading.reset(atcBtn, idle); }
            else { atcBtn.classList.remove("adding"); if (label) label.textContent = idle; }
            showRefusal(refusalText(err));
          });
      });
    }

    /* ---- mobile sticky bar add ---- */
    var mAtc = $("[data-m-atc]");
    if (mAtc) {
      mAtc.addEventListener("click", function () {
        if (mAtc.disabled || mAtc.__busy) return;
        var v = currentVariant();
        if (!v || !v.available) return;
        mAtc.__busy = true;
        var mStatus = $("[data-atc-status]");
        // Watchdog (founder-reported stuck-"Adding"): whatever happens to the request
        // or the loading engine, the button must never wedge — if it is still busy
        // after 14s, hard-reset it so the shopper can try again.
        var mLabel = mAtc.querySelector("[data-m-atc-label]") || mAtc;
        var mDog = setTimeout(function () {
          if (!mAtc.__busy) return;
          if (window.TPWLoading) { try { TPWLoading.reset(mAtc, "Add to cart"); } catch (e) {} }
          if (mLabel.textContent && mLabel.textContent.indexOf("Adding") > -1) mLabel.textContent = "Add to cart";
          mAtc.__busy = false;
        }, 14000);
        // P1 beam: the mobile bar matches the desktop ATC language (white beam + drawn tick).
        // X-6: the label is its own span (labelEl) so the beam layers on the button survive.
        if (window.TPWLoading) {
          TPWLoading.start(mAtc, { shape: "wide", onColor: "dark", label: "Adding", labelEl: mLabel,
            status: mStatus, statusText: "Adding to cart" });
        } else { mLabel.textContent = "Adding"; }
        // Argus-01: the mobile bar shows a single "Add to cart" with no qty control,
        // so it must add 1 (not silently inherit the desktop dial's quantity).
        addToCartAjax(v.id, 1)
          .then(function () {
            clearTimeout(mDog);
            var mLine = ownLine();
            if (window.TPWLoading) {
              TPWLoading.done(mAtc, { tick: false, label: mLine, labelEl: mLabel, status: mStatus,
                statusText: mLine, hold: 2200, idleLabel: "Add to cart" });
              setTimeout(function () { mAtc.__busy = false; }, 2200);
            } else {
              mAtc.classList.add("is-added"); mLabel.textContent = mLine;
              setTimeout(function () { mAtc.classList.remove("is-added"); mLabel.textContent = "Add to cart"; mAtc.__busy = false; }, 2200);
            }
          })
          .catch(function (err) {
            clearTimeout(mDog);
            if (window.TPWLoading) { TPWLoading.reset(mAtc, "Add to cart"); }
            else { mLabel.textContent = "Add to cart"; }
            mAtc.__busy = false;
            showRefusal(refusalText(err), "[data-mbar]");
          });
      });
    }

    /* ---- CRO-02 desktop strip add ----
       Same wiring as the mobile bar above: the SHARED addToCartAjax pipeline (12s
       abort inside it), TPWLoading beam states, a 14s watchdog so the button can
       never wedge on "Adding", and always quantity 1 (Argus-01: a control that
       shows no qty must add exactly 1, never inherit the Dial's count). */
    var dAtcBtn = $("[data-d-atc]");
    if (dAtcBtn) {
      dAtcBtn.addEventListener("click", function () {
        if (dAtcBtn.disabled || dAtcBtn.__busy) return;
        var v = currentVariant();
        if (!v || !v.available) return;
        dAtcBtn.__busy = true;
        var dStatus = $("[data-atc-status]");
        var dLabel = dAtcBtn.querySelector("[data-d-atc-label]") || dAtcBtn;
        var dDog = setTimeout(function () {
          if (!dAtcBtn.__busy) return;
          if (window.TPWLoading) { try { TPWLoading.reset(dAtcBtn, "Add to cart"); } catch (e) {} }
          if (dLabel.textContent && dLabel.textContent.indexOf("Adding") > -1) dLabel.textContent = "Add to cart";
          dAtcBtn.__busy = false;
        }, 14000);
        if (window.TPWLoading) {
          TPWLoading.start(dAtcBtn, { shape: "wide", onColor: "dark", label: "Adding", labelEl: dLabel,
            status: dStatus, statusText: "Adding to cart" });
        } else { dLabel.textContent = "Adding"; }
        addToCartAjax(v.id, 1)
          .then(function () {
            clearTimeout(dDog);
            var dLine = ownLine();
            if (window.TPWLoading) {
              TPWLoading.done(dAtcBtn, { tick: false, label: dLine, labelEl: dLabel, status: dStatus,
                statusText: dLine, hold: 2200, idleLabel: "Add to cart" });
              setTimeout(function () { dAtcBtn.__busy = false; }, 2200);
            } else {
              dLabel.textContent = dLine;
              setTimeout(function () { dLabel.textContent = "Add to cart"; dAtcBtn.__busy = false; }, 2200);
            }
          })
          .catch(function (err) {
            clearTimeout(dDog);
            if (window.TPWLoading) { TPWLoading.reset(dAtcBtn, "Add to cart"); }
            else { dLabel.textContent = "Add to cart"; }
            dAtcBtn.__busy = false;
            showRefusal(refusalText(err), "[data-dbar]");
          });
      });
    }

    /* ---- F-20 "THE LEDGER": below-fold single-column accordion ----
       Progressive enhancement: no-JS renders every panel OPEN + crawlable. With JS we
       add .acc--js, collapse the closed panels and animate open/close by explicit height,
       then settle an open panel to height:auto so the live delivery estimator can grow
       inside it with no layout shift. Panel 01 (.is-open) starts open. Each row header is a
       real <button> with aria-expanded; Enter/Space toggle it natively. Reduced-motion safe. */
    function setupAccordion() {
      var acc = $("[data-acc]");
      if (!acc) return;
      acc.classList.add("acc--js");
      $$("[data-acc-item]").forEach(function (item) {
        var panel = item.querySelector("[data-acc-panel]");
        var btn = item.querySelector("[data-acc-trigger]");
        if (!panel || !btn) return;
        // initial state: an item with .is-open starts open; all items are closed by default (founder's choice)
        panel.style.height = item.classList.contains("is-open") ? "auto" : "0px";
        btn.addEventListener("click", function () {
          var opening = !item.classList.contains("is-open");
          item.classList.toggle("is-open", opening);
          btn.setAttribute("aria-expanded", opening ? "true" : "false");
          if (REDUCED) { panel.style.height = opening ? "auto" : "0px"; return; }
          if (opening) {
            panel.style.height = panel.scrollHeight + "px";
            var settle = function (e) {
              if (e.target !== panel || e.propertyName !== "height") return;
              panel.style.height = "auto"; // let dynamic content grow freely
              panel.removeEventListener("transitionend", settle);
            };
            panel.addEventListener("transitionend", settle);
          } else {
            panel.style.height = panel.scrollHeight + "px"; // from auto -> fixed
            panel.getBoundingClientRect();                  // force reflow
            requestAnimationFrame(function () { panel.style.height = "0px"; });
          }
        });
      });
    }

    /* ---- same-day dispatch FOMO (live countdown to the 2pm AusPost cut-off) ----
       All timing comes from the shared engine window.TPWStoreHours (single
       source of truth: 2pm cut-off, weekend + VIC public-holiday aware). */
    function setupFomo() {
      var el = $("[data-fomo]"); if (!el) return;
      var txt = el.querySelector("[data-fomo-txt]");
      function pad(n) { return n < 10 ? "0" + n : "" + n; }
      function tick() {
        var S = window.TPWStoreHours; if (!S) return;
        var d = S.dispatchETA();
        if (d.sameDay) {
          var s = Math.max(0, d.secondsToCutoff);
          var hh = Math.floor(s / 3600), mm = Math.floor((s % 3600) / 60), ss = s % 60;
          // reduced-motion: drop the per-second ticking (motion-equivalent distraction)
          var t = REDUCED
            ? (hh > 0 ? hh + "h " + pad(mm) + "m" : mm + "m")
            : (hh > 0 ? hh + "h " + pad(mm) + "m " + pad(ss) + "s" : mm + "m " + pad(ss) + "s");
          txt.innerHTML = "Order within <b>" + t + "</b> for same-day dispatch";
          el.classList.add("is-urgent");
        } else {
          // shipsLabel is "Ships tomorrow" / "Ships Monday"; show just the day in bold.
          txt.innerHTML = "Order now &middot; ships <b>" + d.shipsLabel.replace(/^Ships\s+/, "") + "</b>";
          el.classList.remove("is-urgent");
        }
        el.hidden = false;
      }
      var timer = null;
      function start() { if (timer) return; tick(); timer = setInterval(tick, REDUCED ? 60000 : 1000); }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      start();
      // don't tick against a hidden tab, and never leak the interval on section re-render
      document.addEventListener("visibilitychange", function () { if (document.hidden) stop(); else start(); });
      document.addEventListener("shopify:section:unload", function (e) { if (e.target && (e.target === root || (e.target.contains && e.target.contains(root)))) stop(); });
    }

    /* ---- Four Ways to Shop P1: concierge availability line + video prefill ----
       The server renders the closed-safe state (generic "message anytime" copy +
       the video href WITHOUT "I'm free now."). This upgrades both live from the
       store-hours single source of truth (window.TPWStoreHours, sync in <head>),
       then re-evaluates on a 60s tick and on tab return. Chips are never hidden
       when closed; only the expectation copy and the video prefill change. */
    function setupConcierge() {
      var bar = $("[data-concierge]");
      if (!bar || !window.TPWStoreHours) return;
      var status = bar.querySelector("[data-cc-status]");
      var video = bar.querySelector("[data-cc-video]");
      var hrefBase = video ? video.getAttribute("href") : null;
      function apply() {
        var s = window.TPWStoreHours.openStatus();
        if (status) {
          var txt;
          if (s.open) {
            txt = "Open now. Replies in minutes, usually.";
          } else {
            // openLabel (closed) = nextOpen().label, e.g. "Opens 11am tomorrow"
            var back = (s.openLabel || "").replace(/^Opens\s+/i, "");
            txt = back
              ? "Closed now, back at " + back + ". Message anytime and we'll reply first thing."
              : "Message anytime and we'll reply first thing.";
          }
          if (status.textContent !== txt) status.textContent = txt;
        }
        if (video && hrefBase) {
          // "I'm free now." is only honest while the store is open and not closing soon
          var now = video.getAttribute("data-href-now");
          var want = (s.open && !s.closingSoon && now) ? now : hrefBase;
          if (video.getAttribute("href") !== want) video.setAttribute("href", want);
        }
      }
      apply();
      var timer = null;
      function start() { if (!timer) timer = setInterval(apply, 60000); }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      start();
      document.addEventListener("visibilitychange", function () {
        if (document.hidden) { stop(); } else { apply(); start(); }
      });
      document.addEventListener("shopify:section:unload", function (e) { if (e.target && (e.target === root || (e.target.contains && e.target.contains(root)))) stop(); });
    }

    /* ---- X-6 sticky HANDOFF (lane P, 2026-10-04) ----
       The bar must show at EVERY scroll position where the in-flow primary is not fully usable: observe the
       primary itself (the dial, not the whole .actions zone, which stayed "visible" through Shop Pay while the
       Add had already slid under the header), with the top margin = the sticky header's live height and the
       bottom margin = the bar's own height, threshold 1 (any clipping = handoff). Rebuilt on resize because an
       IntersectionObserver's rootMargin is fixed at construction. */
    function stickyHeaderHeight() {
      // the theme's <sticky-header class="header-wrapper"> pins itself (position:fixed) once the page scrolls, so its
      // position at setup time is not the test: take its box height (the uncondensed height is the conservative one).
      var el = document.querySelector("sticky-header") || document.querySelector(".header-wrapper") || document.querySelector(".shopify-section-header");
      return el ? Math.round(el.getBoundingClientRect().height) : 0;
    }
    function watchPrimary(target, bar, onChange, extraBottom) {
      var io = null;
      function build() {
        if (io) io.disconnect();
        var top = stickyHeaderHeight();
        var bottom = extraBottom != null ? extraBottom : Math.round(bar.getBoundingClientRect().height || 0);
        io = new IntersectionObserver(function (ents) {
          ents.forEach(function (en) { onChange(!(en.isIntersecting && en.intersectionRatio >= 0.999)); });
        }, { rootMargin: "-" + top + "px 0px -" + bottom + "px 0px", threshold: [0, 0.999, 1] });
        io.observe(target);
      }
      build();
      var rt = null;
      window.addEventListener("resize", function () { clearTimeout(rt); rt = setTimeout(build, 150); }, { passive: true });
      return build;
    }
    /* ---- mobile sticky bar visibility ---- */
    function setupMobileBar() {
      var bar = $("[data-mbar]"), zone = $("[data-atcdial]") || $("[data-atc]") || $(".actions");
      if (!bar || !zone || !("IntersectionObserver" in window)) return;
      /* Portal the bar to a body-level .pdpv9 host so position:fixed is relative to
         the VIEWPORT. Nested in the section it got contained on iOS and floated
         mid-page; a <body> child cannot. The host carries the .pdpv9 CSS vars so
         every .pdpv9 .mbar rule still applies. (sticky-bar rebuild 2026-06-24) */
      var oldHost = document.querySelector(".pdpv9-mbar-host");
      if (oldHost) oldHost.remove();
      var host = document.createElement("div");
      host.className = "pdpv9 pdpv9-mbar-host";
      document.body.appendChild(host);
      host.appendChild(bar);
      /* M-A02 (Hermes): at max scroll the fixed bar permanently buried the footer's last
         rows (copyright cut mid-sentence, sister-store link untappable). Release the bar
         when the TRUE document end enters the viewport: a 1px sentinel appended to <body>
         (robust against whatever section happens to render last), watched by a second
         IntersectionObserver. Bar shows only when past the buy zone AND not at the end;
         hiding reuses the existing translateY(110%) slide, so it glides away at the foot
         and returns as soon as the user scrolls back up. */
      var oldEnd = document.querySelector(".pdpv9-mbar-end");
      if (oldEnd) oldEnd.remove();
      var end = document.createElement("div");
      end.className = "pdpv9-mbar-end";
      end.setAttribute("aria-hidden", "true");
      end.style.cssText = "height:1px;margin:0;padding:0;border:0";
      document.body.appendChild(end);
      var pastBuy = false, atEnd = false;
      function applyBar() {
        var show = pastBuy && !atEnd;
        bar.classList.toggle("show", show);
        bar.setAttribute("aria-hidden", show ? "false" : "true"); // expose the visible bar to AT
      }
      watchPrimary(zone, bar, function (gone) { pastBuy = gone; applyBar(); });
      var ioEnd = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { atEnd = en.isIntersecting; });
        applyBar();
      });
      ioEnd.observe(end);
    }

    /* ---- CRO-02 "The Dock": desktop sticky buy strip visibility ----
       Mirrors setupMobileBar's twin-sentinel pattern (do not fold them together:
       the mbar is QA-frozen). Shows only when the buy zone has scrolled away AND
       the true document end is not in view; CSS gates it to >=990px. The bar is
       PORTALED to a body-level .pdpv9 host so position:fixed anchors to the
       viewport (a transformed ancestor - e.g. the tpw-reveal engine - would
       otherwise capture it, the same iOS float bug the mbar rebuild fixed).
       Pinned FLUSH to the viewport bottom via CSS (bottom:0, founder 2026-07-04);
       visibility toggles transform + aria-hidden + inert only - fixed overlay,
       zero CLS. */
    function setupDesktopBar() {
      var bar = $("[data-dbar]"), zone = $("[data-atcdial]") || $("[data-atc]") || $(".actions");
      if (!bar || !zone || !("IntersectionObserver" in window)) return;
      var oldHost = document.querySelector(".pdpv9-dbar-host");
      if (oldHost) oldHost.remove();
      var host = document.createElement("div");
      host.className = "pdpv9 pdpv9-dbar-host";
      document.body.appendChild(host);
      host.appendChild(bar);
      var oldEnd = document.querySelector(".pdpv9-dbar-end");
      if (oldEnd) oldEnd.remove();
      var end = document.createElement("div");
      end.className = "pdpv9-dbar-end";
      end.setAttribute("aria-hidden", "true");
      end.style.cssText = "height:1px;margin:0;padding:0;border:0";
      document.body.appendChild(end);
      var pastBuy = false, atEnd = false, shown = false;
      function applyBar() {
        var show = pastBuy && !atEnd;
        shown = show;
        bar.classList.toggle("show", show);
        bar.setAttribute("aria-hidden", show ? "false" : "true");
        // a hidden fixed bar must also leave the tab order (it is only translated
        // off-screen); inert covers focus + AT in one move where supported
        if ("inert" in HTMLElement.prototype) bar.inert = !show;
      }
      applyBar();
      /* X-6 handoff: the primary counts as "gone" the moment any of it is under the live header height or
         behind the dock's own 57 px (watchPrimary above). */
      watchPrimary(zone, bar, function (gone) { pastBuy = gone; applyBar(); });
      var ioEnd = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { atEnd = en.isIntersecting; });
        applyBar();
      });
      ioEnd.observe(end);
      // Back to top: return to the buy column; move focus to the H1 so keyboard
      // users are not stranded inside a bar that is about to inert itself.
      var topBtn = bar.querySelector("[data-dbar-top]");
      if (topBtn) {
        topBtn.addEventListener("click", function () {
          window.scrollTo({ top: 0, behavior: REDUCED ? "auto" : "smooth" });
          var h1 = root.querySelector(".ptitle");
          if (h1) { h1.setAttribute("tabindex", "-1"); h1.focus({ preventScroll: true }); }
        });
      }
    }

    /* recommendations: complementary -> related fallback -> hide-if-empty is now
       handled deterministically by the theme's own <product-recommendations>
       element (assets/global.js), in the same fetch chain and with Swiper init.
       The old timer-based fallback here raced that handler and is retired. */

    /* ---- init ---- */
    // K11: the hero's two neighbours are fetched after load, in idle time, so the first swipe is instant without
    // taxing the load (the hero is the LCP and the only gallery byte at load).
    (function () {
      var warm = function () { preloadAdjacentInline(state.frame); };
      var idle = function () { if ("requestIdleCallback" in window) requestIdleCallback(warm, { timeout: 2500 }); else setTimeout(warm, 1200); };
      if (document.readyState === "complete") idle(); else window.addEventListener("load", idle, { once: true });
    })();
    setupStage();
    setupSwipe(stage, function (dir) { goFrame(state.frame + dir, dir); });   // FI-06: stage swipe (directional)
    setupSwipe(lbStage, lbGo);                                           // FI-06: zoomed swipe
    setupStripDrag();
    setupLightbox();
    setupMobileBar();
    setupDesktopBar();   // CRO-02 desktop sticky buy strip
    setupAccordion();
    setupFomo();
    setupConcierge();
    setupQty();
    updateMeta();
    // Flash F5: no neighbour warm here any more. At init it hydrated slide 2 at High priority while the hero (the LCP) was
    // still downloading (43 KB on the family PDP, measured). The idle-after-load warm above makes the first swipe instant.
    updatePrice(false);
    fillBnplDates();   // BNPL timeline fortnight dates (no-JS falls back to "2 wks" etc.)
    // BNPL gold-edge button: tap to grow the same-colour panel (content already in the DOM)
    var bnplToggle = $("[data-bnpl-toggle]"), bnplBox = $("[data-bnpl]");
    if (bnplToggle && bnplBox) {
      bnplToggle.addEventListener("click", function () {
        var open = bnplBox.classList.toggle("is-open");
        bnplToggle.setAttribute("aria-expanded", open ? "true" : "false");
      });
    }

    // F-24 note: the on-load price roll-IN was REMOVED (founder 2026-06-26). Its
    // rAF-deferred roll($00 -> price) could be starved on this theme (the footer map
    // blocks document_idle, rAF stalls), leaving the reel stuck mid-spin — the headline
    // rendered garbage like "$1010.95" for an $11.95 product. updatePrice(false) above
    // already shapes the CORRECT price via TPWRoll.set on load; the price still ROLLS on
    // variant change (updatePrice(true)) and qty change (bumpAtcPrice). No on-load count-up.
  }
})();
